// src/pages/AddVisit.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, IconButton, MenuItem,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import http from '../services/http';
import {
  COMPLAINT_OPTIONS,
  DURATION_UNITS,
  emptyChiefComplaint,
  findComplaintDefinition,
  normalizeChiefComplaintRows,
} from '../clinical/visitComplaintRegistry';
import {
  derivePrimaryClinicalContext,
  formatChiefComplaintSummary,
} from '../clinical/visitContextEngine';
import { DEFAULT_RULE_PACK } from '../clinical/visitGuidelineRegistry';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getTenantBase() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return match ? match[0] : '';
  } catch {
    return '';
  }
}

function tenantUrl(path) {
  const value = String(path || '');
  return `${getTenantBase()}${value.startsWith('/') ? value : `/${value}`}`.replace(/\/{2,}/g, '/');
}

function safeObject(value, fallback = {}) {
  try {
    const parsed = JSON.parse(value || '');
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function readObject(key, fallback = {}) {
  try {
    return safeObject(localStorage.getItem(key), fallback);
  } catch {
    return fallback;
  }
}

function sanitizeId(value) {
  if (value === undefined || value === null) return '';
  const result = String(value).trim();
  return result === 'undefined' || result === 'null' ? '' : result;
}

function draftKey(citizenId) {
  const id = sanitizeId(citizenId);
  return id ? `addVisitDraft:${id}` : 'addVisitDraft';
}

function readDraft(citizenId) {
  const id = sanitizeId(citizenId);
  const scoped = readObject(draftKey(id), {});
  if (Object.keys(scoped).length) return scoped;

  const legacy = readObject('addVisitDraft', {});
  const legacyCitizenId = sanitizeId(legacy.citizenId);
  return !id || legacyCitizenId === id ? legacy : {};
}

function writeDraft(citizenId, value) {
  try {
    const id = sanitizeId(citizenId);
    const payload = { ...value, citizenId: id || undefined };
    localStorage.setItem(draftKey(id), JSON.stringify(payload));
    localStorage.setItem('addVisitDraft', JSON.stringify(payload));
  } catch {}
}

function removeDraft(citizenId) {
  try {
    localStorage.removeItem(draftKey(citizenId));
    localStorage.removeItem('addVisitDraft');
  } catch {}
}

const CANONICAL_CENTRES = ['CRHA Keru', 'CRHA Dhawa', 'CRHA Dhundhara', 'UHTC Pratap Nagar'];

function normalizeCentre(value) {
  const input = String(value || '').trim();
  if (!input) return '';

  const key = input.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (key.includes('keru')) return 'CRHA Keru';
  if (key.includes('dhawa')) return 'CRHA Dhawa';
  if (key.includes('pratap')) return 'UHTC Pratap Nagar';
  if (['dundhara', 'dhundhara', 'dundara', 'dhundara'].some((item) => key.includes(item))) {
    return 'CRHA Dhundhara';
  }
  return input;
}

function normalizeGender(value) {
  const result = String(value || '').trim().toLowerCase();
  if (!result) return 'unknown';
  if (['male', 'm', 'man', 'boy'].includes(result)) return 'male';
  if (['female', 'f', 'woman', 'girl'].includes(result)) return 'female';
  if (result.includes('trans')) return 'transgender';
  if (['non-binary', 'non binary', 'nonbinary'].some((item) => result.includes(item))) return 'other';
  return result;
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  const ymd = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);

  let parsed;
  if (dmy) parsed = new Date(Date.UTC(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])));
  else if (ymd) parsed = new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])));
  else parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysBetween(fromDate, toDate = new Date()) {
  if (!fromDate) return null;
  const from = Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), fromDate.getUTCDate());
  const to = Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate());
  const days = Math.floor((to - from) / 86400000);
  return Number.isFinite(days) && days >= 0 ? days : null;
}

function numericValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const match = String(value ?? '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : null;
}

function ageValueToDays(value, explicitUnit = '') {
  const number = numericValue(value);
  if (number === null || number < 0) return null;

  const raw = `${value ?? ''} ${explicitUnit ?? ''}`.toLowerCase();
  if (raw.includes('day') || /\b\d+\s*d\b/.test(raw)) return Math.round(number);
  if (raw.includes('week') || /\b\d+\s*w\b/.test(raw)) return Math.round(number * 7);
  if (raw.includes('month') || /\b\d+\s*mo\b/.test(raw)) return Math.round(number * 30.4375);
  return Math.round(number * 365.2425);
}

function normalizeAgeUnit(value) {
  const unit = String(value || '').trim().toLowerCase();
  if (!unit) return '';
  if (['day', 'days', 'd'].includes(unit)) return 'days';
  if (['week', 'weeks', 'w'].includes(unit)) return 'weeks';
  if (['month', 'months', 'm', 'mo'].includes(unit)) return 'months';
  if (['year', 'years', 'y', 'yr', 'yrs'].includes(unit)) return 'years';
  return unit;
}

function deriveAgeContext(citizen = {}, asOnDate = new Date()) {
  const dateOfBirth = parseDate(
    citizen.dateOfBirth || citizen.dob || citizen.birthDate || citizen.date_of_birth
  );
  const ageAsOnDate = parseDate(citizen.ageAsOnDate || citizen.ageRecordedAt);

  let ageDays = dateOfBirth ? daysBetween(dateOfBirth, asOnDate) : null;
  let ageValue = numericValue(citizen.ageValue);
  let ageUnit = normalizeAgeUnit(citizen.ageUnit || citizen.ageUnits || citizen.age_unit);

  if (ageDays === null) {
    const directDays = numericValue(
      citizen.ageInDays ?? citizen.ageDays ?? citizen.age_in_days
    );
    if (directDays !== null && directDays >= 0) ageDays = Math.round(directDays);
  }

  if (ageDays === null) {
    const directMonths = numericValue(
      citizen.ageInMonths ?? citizen.ageMonths ?? citizen.age_in_months
    );
    if (directMonths !== null && directMonths >= 0) {
      ageDays = Math.round(directMonths * 30.4375);
      if (ageValue === null) ageValue = directMonths;
      if (!ageUnit) ageUnit = 'months';
    }
  }

  if (ageDays === null && ageValue !== null) {
    ageDays = ageValueToDays(ageValue, ageUnit || 'years');

    // Reported age is valid as of ageAsOnDate. Carry it forward to the Visit date.
    if (ageDays !== null && ageAsOnDate) {
      const elapsedDays = daysBetween(ageAsOnDate, asOnDate);
      if (elapsedDays !== null) ageDays += elapsedDays;
    }
  }

  if (ageDays === null) {
    const legacyAge = numericValue(citizen.age);
    if (legacyAge !== null && legacyAge >= 0) {
      ageValue = legacyAge;
      ageUnit = ageUnit || 'years';
      ageDays = ageValueToDays(legacyAge, ageUnit);
    }
  }

  let group = 'unknown';
  if (ageDays !== null) {
    if (ageDays <= 27) group = 'newborn';
    else if (ageDays <= 59) group = 'youngInfant';
    else if (ageDays < 365.2425) group = 'infant';
    else if (ageDays < 5 * 365.2425) group = 'under5';
    else if (ageDays < 10 * 365.2425) group = 'child';
    else if (ageDays < 20 * 365.2425) group = 'adolescent';
    else if (ageDays < 60 * 365.2425) group = 'adult';
    else group = 'geriatric';
  }

  let display = 'Age not available';
  if (ageDays !== null && ageDays < 60) {
    display = `${ageDays} day${ageDays === 1 ? '' : 's'}`;
  } else if (ageDays !== null && ageDays < 365.2425) {
    const months = Math.max(2, Math.floor(ageDays / 30.4375));
    const remainingDays = Math.max(0, Math.floor(ageDays - (months * 30.4375)));
    display = `${months} month${months === 1 ? '' : 's'}${remainingDays ? ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''}`;
  } else if (ageDays !== null) {
    const years = Math.floor(ageDays / 365.2425);
    const remainingDays = ageDays - Math.floor(years * 365.2425);
    const months = Math.max(0, Math.floor(remainingDays / 30.4375));
    display = `${years} year${years === 1 ? '' : 's'}${months ? ` ${months} month${months === 1 ? '' : 's'}` : ''}`;
  }

  return {
    dateOfBirth,
    dobStatus: String(citizen.dobStatus || '').trim(),
    ageValue,
    ageUnit: ageUnit || (ageValue !== null ? 'years' : ''),
    ageSource: String(
      citizen.ageSource || (dateOfBirth ? 'date_of_birth' : ageValue !== null ? 'reported' : '')
    ).trim(),
    ageApproximate: Boolean(citizen.ageIsApproximate),
    ageDays,
    ageMonths:
      ageDays === null ? null : Math.round((ageDays / 30.4375) * 100) / 100,
    ageYears:
      ageDays === null ? null : Math.round((ageDays / 365.2425) * 100) / 100,
    group,
    display,
  };
}

function responsePayload(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function extractCitizen(response) {
  const payload = responsePayload(response) || {};
  return payload.citizen || payload.data?.citizen || payload.data || payload;
}

function actorSnapshot(user = {}) {
  return {
    id: sanitizeId(user.userId || user._id || user.id || user.uid || user.sub),
    name: String(user.name || user.fullName || user.username || '').trim(),
    role: String(user.role || user.userType || user.type || '').trim(),
    email: String(user.email || '').trim(),
  };
}

function deriveVisitRegistrationSource(citizen = {}, draft = {}) {
  const explicit = String(draft.registrationSource || '').trim().toLowerCase();
  if (['cmfm_direct', 'state_hmis'].includes(explicit)) return explicit;

  // Every CMFM OPD citizen may already carry State-system identifiers. Those
  // identifiers do NOT by themselves mean the citizen was referred to CMFM.
  // Use the referral flag only; keep the existing codes for backend compatibility.
  return citizen.referredToCmfm === true ? 'state_hmis' : 'cmfm_direct';
}

const AGE_GROUP_LABELS = {
  newborn: 'Newborn (0–27 days)',
  youngInfant: 'Young infant (28–59 days)',
  infant: 'Infant (2–11 months)',
  under5: 'Under-5 child (1–4 years)',
  child: 'Child (5–9 years)',
  adolescent: 'Adolescent (10–19 years)',
  adult: 'Adult (20–59 years)',
  geriatric: 'Older person (60+ years)',
  unknown: 'Age group not derived',
};

function normalizeMaternalContext(value) {
  const key = String(value || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  if (['pregnant', 'pregnancy', 'antenatal', 'anc', 'currently-pregnant'].includes(key)) return 'pregnant';
  if (['postnatal', 'postpartum', 'pnc', 'recent-delivery', 'recently-delivered'].includes(key)) return 'postnatal';
  if (['not-pregnant', 'not-postnatal', 'neither', 'none', 'no'].includes(key)) return 'neither';
  return 'unspecified';
}

function deriveMaternalContext(citizen = {}) {
  const direct = normalizeMaternalContext(
    citizen.maternalContext || citizen.pregnancyStatus || citizen.reproductiveStatus || citizen.obstetricStatus
  );
  if (direct !== 'unspecified') return direct;
  if (citizen.isPregnant === true || citizen.pregnant === true || citizen.currentlyPregnant === true) return 'pregnant';
  if (citizen.isPostnatal === true || citizen.postnatal === true || citizen.postpartum === true) return 'postnatal';

  const deliveryDate = parseDate(citizen.deliveryDate || citizen.dateOfDelivery || citizen.lastDeliveryDate);
  const daysSinceDelivery = deliveryDate ? daysBetween(deliveryDate) : null;
  return daysSinceDelivery !== null && daysSinceDelivery <= 42 ? 'postnatal' : 'unspecified';
}

function getCitizenContext(citizenIdParam, queryCitizenId) {
  const requestedCitizenId = sanitizeId(citizenIdParam || queryCitizenId);
  const storedCitizen = readObject('activeCitizen', {});
  const storedCitizenId = sanitizeId(storedCitizen.citizenId);
  const citizenId = sanitizeId(requestedCitizenId || storedCitizenId || localStorage.getItem('lastVisitedCitizenId'));
  const mismatch = Boolean(requestedCitizenId && storedCitizenId && requestedCitizenId !== storedCitizenId);

  return {
    citizenId,
    mismatch,
    citizen: mismatch ? { citizenId: requestedCitizenId } : { ...storedCitizen, citizenId },
    draft: readDraft(citizenId),
    centre: normalizeCentre(localStorage.getItem('centre') || localStorage.getItem('activeCentre') || ''),
  };
}

const VISIT_TYPES = [
  'New OPD Visit',
  'Follow-up Visit',
  'Procedure / Counseling Visit',
  'Review Visit',
  'Emergency / Urgent Visit',
];

const TRIAGE_PRIORITIES = [
  ['routine', 'Routine'],
  ['priority', 'Priority review required'],
  ['emergency', 'Emergency — immediate senior/MO review'],
];

const MATERNAL_CONTEXT_OPTIONS = [
  ['unspecified', 'Not specified'],
  ['pregnant', 'Currently pregnant / antenatal'],
  ['postnatal', 'Postnatal / within 6 weeks of delivery'],
  ['neither', 'Neither pregnant nor postnatal'],
];

const REGISTRATION_SOURCE_OPTIONS = [
  ['cmfm_direct', 'Attended CMFM after State OPD registration'],
  ['state_hmis', 'Referred / assigned by State MO or CHC to CMFM'],
];

const VISIT_CATEGORY_CONFIG = [
  { key: 'General OPD', ageGroups: ['all'], sexes: ['all'] },
  { key: 'Newborn / Young Infant Care', ageGroups: ['newborn', 'youngInfant'], sexes: ['all'] },
  { key: 'Under-5 / Child Health', ageGroups: ['infant', 'under5', 'child'], sexes: ['all'] },
  { key: 'Adolescent Health', ageGroups: ['adolescent'], sexes: ['all'] },
  { key: 'Adult Medicine', ageGroups: ['adult'], sexes: ['all'] },
  { key: 'Geriatric Care', ageGroups: ['geriatric'], sexes: ['all'] },
  {
    key: 'Antenatal Care',
    ageGroups: ['adolescent', 'adult'],
    sexes: ['female', 'transgender', 'other', 'unknown'],
    maternalContexts: ['pregnant'],
  },
  {
    key: 'Postnatal Care',
    ageGroups: ['adolescent', 'adult'],
    sexes: ['female', 'transgender', 'other', 'unknown'],
    maternalContexts: ['postnatal'],
  },
  { key: 'NCD Screening / Follow-up', ageGroups: ['adolescent', 'adult', 'geriatric'], sexes: ['all'] },
  { key: 'Occupational Health', ageGroups: ['adolescent', 'adult', 'geriatric'], sexes: ['all'] },
  { key: 'Minor Procedure / Counseling', ageGroups: ['all'], sexes: ['all'] },
  { key: 'LGBTQIA+ Health / Counseling', ageGroups: ['adolescent', 'adult', 'geriatric'], sexes: ['all'] },
];

const PATHWAY_OPTIONS = {
  'General OPD': ['General OPD', 'Acute Symptom Pathway', 'Chronic Symptom Pathway', 'Infection / Fever Pathway', 'Emergency Triage / Stabilization Pathway'],
  'Newborn / Young Infant Care': ['Sick Newborn / Young Infant Pathway', 'Newborn Feeding / Breastfeeding Pathway', 'Neonatal Jaundice Pathway', 'Birth Follow-up / Essential Newborn Care'],
  'Under-5 / Child Health': ['IMNCI / Sick Child Pathway', 'Growth & Nutrition Pathway', 'Immunization / Child Review', 'Developmental Assessment Pathway', 'General Paediatric OPD Pathway'],
  'Antenatal Care': ['ANC Registration / Follow-up', 'High-Risk Pregnancy Review', 'ANC Counseling Pathway', 'Pregnancy Emergency Assessment'],
  'Postnatal Care': ['Postnatal Mother Review', 'Newborn + Mother Review', 'Breastfeeding Assessment / Support', 'Postnatal Counseling Pathway', 'Postnatal Emergency Assessment'],
  'Adolescent Health': ['Adolescent OPD Pathway', 'AFHC / Counseling Pathway', 'Adolescent Anemia / Nutrition Pathway', 'Menstrual / Reproductive Health Pathway', 'Adolescent Mental Health / Psychosocial Pathway'],
  'Adult Medicine': ['General Adult OPD', 'Respiratory Pathway', 'Gastrointestinal Pathway', 'Cardiometabolic Pathway', 'Musculoskeletal / Neurology Pathway', 'Emergency Triage / Stabilization Pathway'],
  'Geriatric Care': ['General Geriatric Pathway', 'NCD + Functional Review', 'Falls / Frailty / Supportive Care', 'Medication / Polypharmacy Review', 'Emergency Triage / Stabilization Pathway'],
  'NCD Screening / Follow-up': ['Hypertension Screening / Follow-up', 'Diabetes Screening / Follow-up', 'Combined NCD Follow-up', 'NCD Risk Assessment / Counseling'],
  'Occupational Health': ['Occupational Exposure Review', 'Respiratory Occupational Health', 'Heat / Work Fitness / Counseling', 'Occupational Injury Assessment'],
  'Minor Procedure / Counseling': ['Procedure Pathway', 'Counseling Pathway', 'Procedure + Counseling Combined', 'Wound / Injury Procedure Pathway'],
  'LGBTQIA+ Health / Counseling': ['Gender-Affirming Counseling Pathway', 'Sexual Health / STI Counseling Pathway', 'Mental Health / Supportive Counseling Pathway', 'General Health Assessment Pathway'],
};

const COMPLAINT_GROUP_OPTIONS = {
  'General OPD': ['Fever / Infection', 'Respiratory Symptoms', 'Chest Pain / Cardiovascular Symptoms', 'Gastrointestinal Symptoms', 'Neurological Symptoms', 'Pain / Musculoskeletal', 'General Weakness / Fatigue', 'Skin / Allergy', 'Injury / Trauma', 'Mental Health / Psychosocial Concern', 'Other General OPD'],
  'Newborn / Young Infant Care': ['Poor Feeding / Not Feeding', 'Jaundice', 'Fever / Hypothermia', 'Breathing Difficulty', 'Reduced Activity / Lethargy', 'Convulsions / Abnormal Movements', 'Vomiting / Abdominal Concern', 'Cord / Skin Problem', 'Birth Follow-up / Routine Newborn Review', 'Other Newborn / Young Infant Concern'],
  'Antenatal Care': ['Routine ANC', 'High-Risk Pregnancy', 'Bleeding / Leaking / Labour Symptoms', 'Reduced Fetal Movement', 'Headache / Visual Symptoms / Swelling', 'Anemia in Pregnancy', 'Pregnancy Counseling', 'Other ANC'],
  'Postnatal Care': ['Routine Postnatal Review', 'Postpartum Bleeding / Fever / Pain', 'Breast Problem', 'Breastfeeding / Latch Difficulty', 'Newborn Complaint', 'Mental Health / Emotional Wellbeing', 'Postnatal Counseling', 'Other PNC'],
  'Under-5 / Child Health': ['General Danger Sign / Very Sick Child', 'Cough / Respiratory', 'Diarrhea / Vomiting', 'Fever / Infection', 'Ear Problem', 'Growth / Nutrition / Malnutrition', 'Feeding / Breastfeeding', 'Immunization Related', 'Developmental Concern', 'Injury / Trauma', 'Other Child Health'],
  'Adolescent Health': ['General Adolescent Complaint', 'Nutrition / Anemia', 'Menstrual / Reproductive Health', 'Sexual Health / STI Concern', 'Counseling / Psychosocial', 'Mental Health Concern', 'Substance Use Concern', 'Other Adolescent'],
  'Adult Medicine': ['Respiratory', 'Chest Pain / Cardiovascular', 'Gastrointestinal', 'Cardiometabolic', 'Neurology', 'Musculoskeletal', 'Dermatology', 'Mental Health / Psychosocial', 'Other Adult Medicine'],
  'Geriatric Care': ['General Geriatric Review', 'NCD Follow-up', 'Pain / Mobility', 'Falls / Dizziness', 'Memory / Cognition', 'Mood / Psychosocial', 'Medication / Polypharmacy', 'Other Geriatric'],
  'NCD Screening / Follow-up': ['Hypertension', 'Diabetes', 'Combined NCD Follow-up', 'Cardiovascular Risk Assessment', 'Risk Assessment / Counseling', 'Other NCD'],
  'Occupational Health': ['Occupational Exposure', 'Respiratory Occupational', 'Heat / Dehydration', 'Injury / Trauma', 'Fitness / Counseling', 'Other Occupational Health'],
  'Minor Procedure / Counseling': ['Wound Care / Suturing', 'Injection / Medication Procedure', 'Dressing / Follow-up Procedure', 'Counseling', 'Procedure + Counseling', 'Other Minor Procedure / Counseling'],
  'LGBTQIA+ Health / Counseling': ['Gender-Affirming Care / Counseling', 'Sexual Health Concern', 'Mental Health / Supportive Counseling', 'General LGBTQIA+ Health Concern'],
};

const RED_FLAG_PROMPTS = {
  'General OPD': ['Altered consciousness, convulsions or new neurological deficit', 'Severe breathlessness, cyanosis or very low oxygen saturation', 'Chest pain with sweating, collapse or haemodynamic instability', 'Uncontrolled bleeding, severe trauma or shock'],
  'Newborn / Young Infant Care': ['Not feeding at all or feeding markedly less', 'Convulsions, lethargy, unconsciousness or reduced movement', 'Severe chest indrawing, apnoea or cyanosis', 'Fever, hypothermia or rapidly worsening jaundice'],
  'Under-5 / Child Health': ['Unable to drink/breastfeed or vomiting everything', 'Convulsions, lethargy or unconsciousness', 'Severe respiratory distress, cyanosis or stridor at rest', 'Severe dehydration, shock or severe malnutrition with complication'],
  'Antenatal Care': ['Heavy bleeding, convulsions or altered consciousness', 'Severe headache with visual symptoms or severe hypertension', 'Reduced/absent fetal movement, severe abdominal pain or labour emergency', 'Severe breathlessness, shock or suspected sepsis'],
  'Postnatal Care': ['Heavy bleeding, shock, convulsions or severe hypertension symptoms', 'High fever, severe abdominal pain or suspected sepsis', 'Severe breast infection with systemic illness', 'Mother or newborn unable to feed, lethargic or critically unwell'],
  'Adolescent Health': ['Self-harm risk, suicidal thoughts or immediate safety concern', 'Severe bleeding, acute abdomen or pregnancy-related emergency', 'Altered consciousness, convulsions or severe respiratory distress'],
  'Adult Medicine': ['Chest pain, severe breathlessness or suspected stroke', 'Altered consciousness, convulsions or severe sepsis', 'Uncontrolled bleeding, shock or major trauma'],
  'Geriatric Care': ['Acute confusion, suspected stroke or collapse', 'Chest pain, severe breathlessness or haemodynamic instability', 'Fall with suspected fracture/head injury or inability to mobilize'],
  'NCD Screening / Follow-up': ['Very high BP with neurological, cardiac or visual symptoms', 'Severe hypo/hyperglycaemia symptoms or altered consciousness', 'Chest pain, stroke symptoms or acute breathlessness'],
  'Occupational Health': ['Acute chemical exposure, severe breathing difficulty or altered sensorium', 'Heat stroke, collapse or severe dehydration', 'Major workplace trauma or uncontrolled bleeding'],
  'Minor Procedure / Counseling': ['Uncontrolled bleeding or shock', 'Neurovascular deficit, tendon injury or deep contaminated wound', 'Severe allergic reaction or procedural complication'],
  'LGBTQIA+ Health / Counseling': ['Self-harm risk, suicidal thoughts or immediate safety concern', 'Sexual assault, acute violence or urgent safeguarding concern', 'Severe medication-related or acute medical symptoms'],
};

function optionsForCategory(category, ageGroup, gender) {
  let pathways = PATHWAY_OPTIONS[category] || ['General OPD'];
  let complaints = COMPLAINT_GROUP_OPTIONS[category] || ['Other'];

  if (category === 'Under-5 / Child Health' && ageGroup === 'child') {
    pathways = pathways.filter((item) => item !== 'IMNCI / Sick Child Pathway');
    complaints = complaints.filter((item) => item !== 'Feeding / Breastfeeding');
  }
  if (category === 'Adolescent Health' && gender === 'male') {
    complaints = complaints.filter((item) => item !== 'Menstrual / Reproductive Health');
  }
  return { pathways, complaints };
}

function categoryAllowed(config, ageGroup, gender, maternalContext) {
  if (!config) return false;
  if (ageGroup === 'unknown' && !['General OPD', 'Minor Procedure / Counseling'].includes(config.key)) return false;
  if (!config.ageGroups.includes('all') && !config.ageGroups.includes(ageGroup)) return false;
  if (!config.sexes.includes('all') && !config.sexes.includes(gender)) return false;
  if (config.maternalContexts && !config.maternalContexts.includes(maternalContext)) return false;
  return true;
}

function showMaternalContext(ageGroup, gender) {
  return ['adolescent', 'adult'].includes(ageGroup) && gender !== 'male';
}

function initialForm(centre, draft, maternalContext, citizen = {}) {
  const draftMaternal = normalizeMaternalContext(draft.maternalContext);
  const registrationSource = deriveVisitRegistrationSource(citizen, draft);

  return {
    presentingComplaint: draft.presentingComplaint || draft.additionalComplaintText || '',
    chiefComplaints: normalizeChiefComplaintRows(
      draft.chiefComplaints,
      Array.isArray(draft.chiefComplaints) && draft.chiefComplaints.length
        ? ''
        : (draft.primaryPresentingProblem || draft.complaintGroup || ''),
      { preserveBlankRows: true }
    ),
    centre: normalizeCentre(draft.centre || centre || citizen.centre),
    visitType: draft.visitType || 'New OPD Visit',
    visitCategory: draft.visitCategory || 'General OPD',
    clinicalPathway: draft.clinicalPathway || '',
    complaintGroup: draft.complaintGroup || '',
    maternalContext: draftMaternal !== 'unspecified' ? draftMaternal : maternalContext,
    triagePriority: ['routine', 'priority', 'emergency'].includes(draft.triagePriority)
      ? draft.triagePriority
      : 'routine',
    redFlagSummary: draft.redFlagSummary || '',

    // State HMIS / external referral bridge. Patient-level identifiers are
    // prefilled from Citizen; Visit-level token/referral values remain editable.
    registrationSource,
    externalSystem:
      draft.externalSystem ||
      citizen.externalSystem ||
      (registrationSource === 'state_hmis' ? 'state_hmis' : ''),
    externalPatientId: draft.externalPatientId || citizen.externalPatientId || '',
    externalRegistrationNumber:
      draft.externalRegistrationNumber || citizen.externalRegistrationNumber || '',
    externalVisitId: draft.externalVisitId || '',
    externalTokenNumber: draft.externalTokenNumber || '',
    externalRegistrationDate: draft.externalRegistrationDate || '',
    referralReason: draft.referralReason || '',
    referredByName: draft.referredByName || '',
    referredByRole: draft.referredByRole || '',
  };
}

export default function AddVisit() {
  const navigate = useNavigate();
  const { citizenId: citizenIdParam } = useParams();
  const query = useQuery();
  const queryCitizenId = query.get('citizenId');

  const initialContext = useMemo(
    () => getCitizenContext(citizenIdParam, queryCitizenId),
    [citizenIdParam, queryCitizenId]
  );

  const [activeCitizen, setActiveCitizen] = useState(initialContext.citizen);
  const [citizenMismatch, setCitizenMismatch] = useState(initialContext.mismatch);
  const [loggedInUser] = useState(() => readObject('loggedInUser', {}));
  const [form, setForm] = useState(() =>
    initialForm(
      initialContext.centre,
      initialContext.draft,
      deriveMaternalContext(initialContext.citizen),
      initialContext.citizen
    )
  );
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const requestedCitizenId = sanitizeId(citizenIdParam || queryCitizenId);
  const citizenId = sanitizeId(
    requestedCitizenId || activeCitizen.citizenId || localStorage.getItem('lastVisitedCitizenId')
  );
  const ageContext = useMemo(() => deriveAgeContext(activeCitizen), [activeCitizen]);
  const gender = useMemo(() => normalizeGender(activeCitizen.gender), [activeCitizen]);
  const maternalContextVisible = showMaternalContext(ageContext.group, gender);
  const effectiveMaternalContext = maternalContextVisible
    ? normalizeMaternalContext(form.maternalContext)
    : 'unspecified';
  const isHmisLinked = form.registrationSource === 'state_hmis';

  const visibleCategories = useMemo(() => {
    const result = VISIT_CATEGORY_CONFIG
      .filter((config) => categoryAllowed(config, ageContext.group, gender, effectiveMaternalContext))
      .map((config) => config.key);
    return result.length ? result : ['General OPD', 'Minor Procedure / Counseling'];
  }, [ageContext.group, gender, effectiveMaternalContext]);

  const category = visibleCategories.includes(form.visitCategory)
    ? form.visitCategory
    : visibleCategories[0] || 'General OPD';
  const { pathways, complaints } = useMemo(
    () => optionsForCategory(category, ageContext.group, gender),
    [category, ageContext.group, gender]
  );
  const chiefComplaints = useMemo(
    () => normalizeChiefComplaintRows(form.chiefComplaints, '', { preserveBlankRows: true }),
    [form.chiefComplaints]
  );
  const meaningfulChiefComplaints = useMemo(
    () => normalizeChiefComplaintRows(chiefComplaints),
    [chiefComplaints]
  );
  const derivedClinicalContext = useMemo(
    () => derivePrimaryClinicalContext({
      serviceArea: category,
      chiefComplaints: meaningfulChiefComplaints,
      clinicalPathway: form.clinicalPathway,
      triagePriority: form.triagePriority,
    }),
    [category, meaningfulChiefComplaints, form.clinicalPathway, form.triagePriority]
  );
  const pathway = derivedClinicalContext.clinicalPathway ||
    (pathways.includes(form.clinicalPathway) ? form.clinicalPathway : '');
  const complaintGroup = derivedClinicalContext.complaintGroup ||
    (complaints.includes(form.complaintGroup) ? form.complaintGroup : '');
  const primaryPresentingProblem = derivedClinicalContext.primaryPresentingProblem || complaintGroup;
  const redFlags = RED_FLAG_PROMPTS[category] || [];

  const centreOptions = useMemo(() => {
    const result = [...CANONICAL_CENTRES];
    const current = normalizeCentre(form.centre);
    if (current && !result.includes(current)) result.unshift(current);
    return result;
  }, [form.centre]);

  const profileSummary = useMemo(() => {
    const result = [];
    if (ageContext.ageDays !== null) result.push(ageContext.display, AGE_GROUP_LABELS[ageContext.group]);
    if (activeCitizen.gender) result.push(String(activeCitizen.gender));
    return result.join(' • ');
  }, [ageContext, activeCitizen]);

  function normalizeForm(next) {
    const allowedCategory = visibleCategories.includes(next.visitCategory)
      ? next.visitCategory
      : visibleCategories[0] || 'General OPD';
    const allowedOptions = optionsForCategory(allowedCategory, ageContext.group, gender);

    // Keep blank editor rows while the user is building a complaint list. The
    // clinical engine still receives only meaningful rows, so an empty row never
    // becomes a pathway or a persisted clinical complaint.
    const normalizedChiefComplaints = normalizeChiefComplaintRows(
      next.chiefComplaints,
      '',
      { preserveBlankRows: true }
    );
    const meaningfulRows = normalizeChiefComplaintRows(normalizedChiefComplaints);
    const derived = derivePrimaryClinicalContext({
      serviceArea: allowedCategory,
      chiefComplaints: meaningfulRows,
      clinicalPathway: next.clinicalPathway,
      triagePriority: next.triagePriority,
    });

    return {
      ...next,
      centre: normalizeCentre(next.centre),
      visitCategory: allowedCategory,
      chiefComplaints: normalizedChiefComplaints,
      clinicalPathway: derived.clinicalPathway ||
        (allowedOptions.pathways.includes(next.clinicalPathway) ? next.clinicalPathway : ''),
      complaintGroup: derived.complaintGroup ||
        (allowedOptions.complaints.includes(next.complaintGroup) ? next.complaintGroup : ''),
      primaryPresentingProblem: derived.primaryPresentingProblem || next.primaryPresentingProblem || '',
      activeClinicalModules: derived.activeClinicalModules || [],
      maternalContext: maternalContextVisible
        ? normalizeMaternalContext(next.maternalContext)
        : 'unspecified',
      triagePriority: ['routine', 'priority', 'emergency'].includes(next.triagePriority)
        ? next.triagePriority
        : 'routine',
      registrationSource: ['cmfm_direct', 'state_hmis'].includes(
        next.registrationSource
      )
        ? next.registrationSource
        : 'cmfm_direct',
      externalSystem:
        next.registrationSource === 'state_hmis'
          ? next.externalSystem || 'state_hmis'
          : next.externalSystem || '',
    };
  }

  function persist(next) {
    writeDraft(citizenId, next);
  }

  useEffect(() => {
    const sync = () => {
      const context = getCitizenContext(citizenIdParam, query.get('citizenId'));
      setActiveCitizen(context.citizen);
      setCitizenMismatch(context.mismatch);
      setForm(
        initialForm(
          context.centre,
          context.draft,
          deriveMaternalContext(context.citizen),
          context.citizen
        )
      );
    };

    sync();
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, [citizenIdParam, query]);

  useEffect(() => {
    let alive = true;

    async function loadCanonicalCitizen() {
      if (!citizenId) {
        setProfileLoading(false);
        setProfileError('');
        return;
      }

      try {
        setProfileLoading(true);
        setProfileError('');
        const response = await http.get(`/api/citizens/${encodeURIComponent(citizenId)}`);
        const citizen = extractCitizen(response);
        const loadedCitizenId = sanitizeId(citizen?.citizenId);

        if (!alive) return;
        if (!loadedCitizenId || loadedCitizenId !== citizenId) {
          setCitizenMismatch(true);
          setProfileError('The loaded citizen profile did not match the selected Citizen ID.');
          return;
        }

        const mergedCitizen = { ...activeCitizen, ...citizen, citizenId: loadedCitizenId };
        setActiveCitizen(mergedCitizen);
        setCitizenMismatch(false);

        setForm((current) => {
          const citizenSource = deriveVisitRegistrationSource(mergedCitizen, {});
          const source =
            current.registrationSource === 'state_hmis' || citizenSource !== 'state_hmis'
              ? current.registrationSource || citizenSource
              : citizenSource;
          return {
            ...current,
            centre: normalizeCentre(current.centre || mergedCitizen.centre),
            registrationSource: source,
            externalSystem:
              current.externalSystem ||
              mergedCitizen.externalSystem ||
              (source === 'state_hmis' ? 'state_hmis' : ''),
            externalPatientId:
              current.externalPatientId || mergedCitizen.externalPatientId || '',
            externalRegistrationNumber:
              current.externalRegistrationNumber ||
              mergedCitizen.externalRegistrationNumber ||
              '',
          };
        });

        try {
          const previous = readObject('activeCitizen', {});
          localStorage.setItem(
            'activeCitizen',
            JSON.stringify({ ...previous, ...mergedCitizen, citizenId: loadedCitizenId })
          );
        } catch {}
      } catch (profileLoadError) {
        if (!alive) return;
        setProfileError(
          profileLoadError?.response?.data?.message ||
            profileLoadError?.message ||
            'Unable to refresh the citizen profile. Stored details will be used.'
        );
      } finally {
        if (alive) setProfileLoading(false);
      }
    }

    loadCanonicalCitizen();
    return () => {
      alive = false;
    };
  }, [citizenId]); // activeCitizen intentionally omitted to avoid a fetch loop

  useEffect(() => {
    const next = normalizeForm({ ...form, visitCategory: category, clinicalPathway: pathway, complaintGroup });
    if (
      next.visitCategory !== form.visitCategory ||
      next.clinicalPathway !== form.clinicalPathway ||
      next.complaintGroup !== form.complaintGroup ||
      next.centre !== form.centre ||
      next.maternalContext !== form.maternalContext ||
      next.triagePriority !== form.triagePriority ||
      next.registrationSource !== form.registrationSource ||
      next.externalSystem !== form.externalSystem
    ) {
      setForm(next);
      persist(next);
    }
  }, [
    citizenId,
    ageContext.group,
    gender,
    effectiveMaternalContext,
    form.maternalContext,
    form.registrationSource,
    form.externalSystem,
    category,
    pathway,
    complaintGroup,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(event) {
    const { name, value } = event.target;

    if (name === 'centre') {
      const next = { ...form, centre: normalizeCentre(value) };
      setForm(next);
      persist(next);
      try {
        localStorage.setItem('centre', next.centre);
        localStorage.setItem('activeCentre', next.centre);
      } catch {}
      return;
    }

    if (name === 'maternalContext') {
      const next = normalizeForm({
        ...form,
        maternalContext: value,
        visitCategory: 'General OPD',
        clinicalPathway: '',
      });
      setForm(next);
      persist(next);
      return;
    }

    if (name === 'visitCategory') {
      const next = normalizeForm({ ...form, visitCategory: value, clinicalPathway: '' });
      setForm(next);
      persist(next);
      return;
    }

    if (name === 'visitType') {
      const next = normalizeForm({
        ...form,
        visitType: value,
        triagePriority: value === 'Emergency / Urgent Visit' ? 'emergency' : form.triagePriority,
      });
      setForm(next);
      persist(next);
      return;
    }

    if (name === 'triagePriority') {
      const next = normalizeForm({
        ...form,
        triagePriority: value,
        visitType: value === 'emergency' ? 'Emergency / Urgent Visit' : form.visitType,
      });
      setForm(next);
      persist(next);
      return;
    }

    if (name === 'registrationSource') {
      const next = normalizeForm({
        ...form,
        registrationSource: value,
        externalSystem: value === 'state_hmis' ? form.externalSystem || 'state_hmis' : form.externalSystem,
      });
      setForm(next);
      persist(next);
      return;
    }

    const next = normalizeForm({ ...form, [name]: value });
    setForm(next);
    persist(next);
  }
  function updateChiefComplaints(nextRows) {
    const normalized = normalizeChiefComplaintRows(nextRows, '', { preserveBlankRows: true });
    const next = normalizeForm({ ...form, chiefComplaints: normalized });
    setForm(next);
    persist(next);
  }

  function addChiefComplaint() {
    updateChiefComplaints([
      ...chiefComplaints,
      emptyChiefComplaint(chiefComplaints.length),
    ]);
  }

  function updateChiefComplaint(index, patch) {
    const rows = chiefComplaints.length ? chiefComplaints.slice() : [emptyChiefComplaint(0)];
    const current = rows[index] || emptyChiefComplaint(index);
    const nextRow = { ...current, ...patch };
    const def = findComplaintDefinition(nextRow.complaintCode || nextRow.complaint);
    if (def) {
      nextRow.complaintCode = def.code;
      nextRow.complaint = def.label;
    }
    rows[index] = nextRow;
    updateChiefComplaints(rows);
  }

  function removeChiefComplaint(index) {
    updateChiefComplaints(chiefComplaints.filter((_row, rowIndex) => rowIndex !== index));
  }

  function moveChiefComplaint(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= chiefComplaints.length) return;
    const rows = chiefComplaints.slice();
    [rows[index], rows[target]] = [rows[target], rows[index]];
    updateChiefComplaints(rows);
  }

  async function createVisit(event) {
    event?.preventDefault?.();

    if (!citizenId) return setError('Citizen selection is required');
    if (!form.centre) return setError('Centre is required');
    if (!category) return setError('Service Area is required');
    if (!pathway) return setError('Unable to determine a clinical pathway');
    if (!meaningfulChiefComplaints.length && ['General OPD', 'Adult Medicine'].includes(category)) {
      return setError('Add at least one chief complaint');
    }
    if (form.triagePriority === 'emergency' && !String(form.redFlagSummary || '').trim()) {
      return setError('Record the emergency red flag or immediate concern before starting the visit');
    }
    if (
      isHmisLinked &&
      !String(form.externalPatientId || '').trim() &&
      !String(form.externalRegistrationNumber || '').trim() &&
      !String(form.externalTokenNumber || '').trim()
    ) {
      return setError(
        'For a State HMIS referral, enter the HMIS patient ID, registration number, or token number.'
      );
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const centre = normalizeCentre(form.centre);
      const visitDate = new Date();
      const actor = actorSnapshot(loggedInUser);
      const effectiveAge = deriveAgeContext(activeCitizen, visitDate);
      const visitMeta = {
        visitType: form.visitType || 'New OPD Visit',
        encounterType: form.visitType || 'New OPD Visit',
        visitCategory: category,
        serviceArea: category,
        clinicalPathway: pathway,
        clinicalFocus: pathway,
        complaintGroup,
        primaryPresentingProblem,
      };
      const encounterContext = {
        ...visitMeta,
        maternalContext: effectiveMaternalContext,
        triagePriority: form.triagePriority || 'routine',
        redFlagSummary: String(form.redFlagSummary || '').trim(),
        redFlagPrompts: redFlags,
        ageGroup: effectiveAge.group,
        ageGroupAtVisit: effectiveAge.group,
        ageInDays: effectiveAge.ageDays === null ? undefined : effectiveAge.ageDays,
        ageInDaysAtVisit:
          effectiveAge.ageDays === null ? undefined : effectiveAge.ageDays,
        ageInMonthsAtVisit:
          effectiveAge.ageMonths === null ? undefined : effectiveAge.ageMonths,
        ageYearsAtVisit:
          effectiveAge.ageYears === null ? undefined : effectiveAge.ageYears,
      };
      const presentingComplaint =
        formatChiefComplaintSummary(meaningfulChiefComplaints, form.presentingComplaint) ||
        primaryPresentingProblem ||
        complaintGroup ||
        'OPD Visit';

      const referredBy = {
        id: actor.id || undefined,
        name: String(form.referredByName || actor.name || '').trim() || undefined,
        role: String(form.referredByRole || actor.role || '').trim() || undefined,
        email: actor.email || undefined,
      };

      const payload = {
        citizenId,
        age:
          effectiveAge.ageYears !== null
            ? effectiveAge.ageYears
            : activeCitizen.age || undefined,
        gender: activeCitizen.gender || activeCitizen.sex || undefined,
        centre,
        presentingComplaint,
        chiefComplaint: presentingComplaint,
        chiefComplaints: meaningfulChiefComplaints.map((row, index) => {
          const def = findComplaintDefinition(row.complaintCode || row.complaint);
          return {
            ...row,
            sequence: index,
            complaintGroup: def?.complaintGroup || undefined,
            pathwayHint: def?.defaultPathway || undefined,
            moduleHints: def?.modules || [],
          };
        }),
        additionalComplaintText: String(form.presentingComplaint || '').trim() || undefined,
        activeClinicalModules: derivedClinicalContext.activeClinicalModules || [],
        clinicalRulePackId: DEFAULT_RULE_PACK.id,
        clinicalRulePackVersion: DEFAULT_RULE_PACK.version,
        visitDate: visitDate.toISOString(),
        workflowMode: 'visit-workspace-v1',
        workflowVersion: 2,
        createdById: actor.id || undefined,
        createdByName: actor.name || undefined,
        createdByRole: actor.role || undefined,
        ...visitMeta,

        // Immutable encounter-age snapshot accepted by the upgraded Visit model.
        dateOfBirthAtVisit: effectiveAge.dateOfBirth
          ? effectiveAge.dateOfBirth.toISOString()
          : undefined,
        dobStatusAtVisit: effectiveAge.dobStatus || undefined,
        ageValueAtVisit:
          effectiveAge.ageValue === null ? undefined : effectiveAge.ageValue,
        ageUnitAtVisit: effectiveAge.ageUnit || undefined,
        ageAsOnDate: visitDate.toISOString(),
        ageSourceAtVisit: effectiveAge.ageSource || undefined,
        ageApproximateAtVisit: effectiveAge.ageApproximate,
        ageInDaysAtVisit:
          effectiveAge.ageDays === null ? undefined : effectiveAge.ageDays,
        ageInMonthsAtVisit:
          effectiveAge.ageMonths === null ? undefined : effectiveAge.ageMonths,
        ageYearsAtVisit:
          effectiveAge.ageYears === null ? undefined : effectiveAge.ageYears,
        ageGroupAtVisit: effectiveAge.group,

        // Encounter-specific maternal and triage context.
        maternalContext: effectiveMaternalContext,
        triagePriority: form.triagePriority || 'routine',
        redFlagSummary: String(form.redFlagSummary || '').trim() || undefined,
        triageAssessedAt: visitDate.toISOString(),
        triageAssessedBy: actor,

        // State HMIS is the primary registration system where applicable.
        // This Visit stores only the linkage/referral context used by CMFM.
        registrationSource: form.registrationSource || 'cmfm_direct',
        externalSystem:
          String(form.externalSystem || '').trim() ||
          (isHmisLinked ? 'state_hmis' : undefined),
        externalPatientId: String(form.externalPatientId || '').trim() || undefined,
        externalRegistrationNumber:
          String(form.externalRegistrationNumber || '').trim() || undefined,
        externalVisitId: String(form.externalVisitId || '').trim() || undefined,
        externalTokenNumber: String(form.externalTokenNumber || '').trim() || undefined,
        externalRegistrationDate:
          parseDate(form.externalRegistrationDate)?.toISOString() || undefined,
        referredToCmfm: isHmisLinked,
        referredToCmfmAt: isHmisLinked ? visitDate.toISOString() : undefined,
        referralReason: String(form.referralReason || '').trim() || undefined,
        referredBy: isHmisLinked ? referredBy : undefined,
      };

      const response = await http.post('/api/visits', payload);
      const data = response?.data || {};
      const visit = data.visit || data;
      const visitId = visit?.visitId || data?.visitId || visit?._id;
      if (!visitId) throw new Error('Visit ID missing in response');

      localStorage.setItem('activeVisitId', visitId);
      localStorage.setItem('visitId', visitId);
      localStorage.setItem(`activeVisitId:${citizenId}`, visitId);
      localStorage.setItem('lastVisitedCitizenId', citizenId);

      try {
        const previous = readObject('activeCitizen', {});
        localStorage.setItem(
          'activeCitizen',
          JSON.stringify({ ...previous, ...activeCitizen, citizenId, centre })
        );
      } catch {}

      const activeVisitContext = {
        visitId,
        citizenId,
        centre,
        presentingComplaint,
        chiefComplaints: payload.chiefComplaints,
        additionalComplaintText: payload.additionalComplaintText,
        activeClinicalModules: payload.activeClinicalModules,
        clinicalRulePackId: payload.clinicalRulePackId,
        clinicalRulePackVersion: payload.clinicalRulePackVersion,
        redFlagPrompts: redFlags,
        registrationSource: payload.registrationSource,
        externalSystem: payload.externalSystem,
        externalPatientId: payload.externalPatientId,
        externalRegistrationNumber: payload.externalRegistrationNumber,
        externalVisitId: payload.externalVisitId,
        externalTokenNumber: payload.externalTokenNumber,
        referralReason: payload.referralReason,
        ...(visit && typeof visit === 'object' ? visit : {}),
        ...encounterContext,
      };

      try {
        localStorage.setItem('activeVisitContext', JSON.stringify(activeVisitContext));
        localStorage.setItem(`activeVisitContext:${visitId}`, JSON.stringify(activeVisitContext));
        removeDraft(citizenId);
        localStorage.setItem('centre', centre);
        localStorage.setItem('activeCentre', centre);
      } catch {}

      setSuccess('Visit created');

      const params = new URLSearchParams({
        visitId,
        section: 'history',
        visitCategory: visitMeta.visitCategory,
        clinicalPathway: visitMeta.clinicalPathway,
        complaintGroup: visitMeta.complaintGroup,
        triagePriority: encounterContext.triagePriority,
        maternalContext: encounterContext.maternalContext,
      });

      navigate(
        tenantUrl(`/citizen/${encodeURIComponent(citizenId)}/visit/${encodeURIComponent(visitId)}/workspace?${params.toString()}`),
        {
          replace: true,
          state: {
            visitId,
            citizenId,
            centre,
            visitContext: activeVisitContext,
            citizen: Object.keys(activeCitizen).length ? activeCitizen : undefined,
          },
        }
      );
    } catch (visitError) {
      setError(visitError?.response?.data?.message || visitError?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  const hmisReferencePresent = Boolean(
    String(form.externalPatientId || '').trim() ||
      String(form.externalRegistrationNumber || '').trim() ||
      String(form.externalTokenNumber || '').trim()
  );

  const disabled =
    loading ||
    profileLoading ||
    !citizenId ||
    !form.centre ||
    !category ||
    !pathway ||
    (['General OPD', 'Adult Medicine'].includes(category) && !primaryPresentingProblem) ||
    (form.triagePriority === 'emergency' && !String(form.redFlagSummary || '').trim()) ||
    (isHmisLinked && !hmisReferencePresent);

  return (
    <Box className="p-6 bg-[#e7f2f8] dark:bg-[#121212] text-black dark:text-white min-h-screen">
      <Typography variant="h5" className="mb-4 text-blue-800 dark:text-blue-200 font-semibold">
        Start a New Visit
      </Typography>

      {activeCitizen.citizenId && (
        <Alert severity="info" className="mb-3">
          {activeCitizen.name || 'Selected Citizen'} |{' '}
          {profileSummary ||
            `Age: ${activeCitizen.age || 'Not available'} | Gender: ${
              activeCitizen.gender || 'Not available'
            }`}{' '}
          | ID: <strong>{activeCitizen.citizenId}</strong>
        </Alert>
      )}

      {citizenMismatch && (
        <Alert severity="warning" className="mb-3">
          The citizen in the URL did not match the previously stored citizen. The route citizen has
          been prioritised so another citizen&apos;s age, gender or draft cannot be used. Re-select
          the citizen if profile details are not visible.
        </Alert>
      )}

      {profileLoading && (
        <Alert severity="info" className="mb-3">
          Refreshing the canonical citizen profile…
        </Alert>
      )}

      {profileError && (
        <Alert severity="warning" className="mb-3">
          {profileError}
        </Alert>
      )}

      {activeCitizen.citizenId && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Age group: ${AGE_GROUP_LABELS[ageContext.group]}`}
          />
          <Chip size="small" variant="outlined" label={`Age: ${ageContext.display}`} />
          <Chip
            size="small"
            variant="outlined"
            label={`Visible service areas: ${visibleCategories.length}`}
          />
        </Box>
      )}

      {!citizenId && (
        <Alert severity="warning" className="mb-3">
          No citizen selected. Choose a citizen from the Comprehensive Services page.
          <Button
            size="small"
            sx={{ ml: 2 }}
            variant="outlined"
            onClick={() => navigate(tenantUrl('/dashboard/operations'))}
          >
            Open Comprehensive Services
          </Button>
        </Alert>
      )}

      {citizenId && ageContext.group === 'unknown' && (
        <Alert severity="warning" className="mb-3">
          The citizen has no usable date of birth, age with unit, age in months or age in days.
          Only general service areas are shown to prevent an incorrect age-specific pathway.
        </Alert>
      )}

      {error && <Alert severity="error" className="mb-3">{error}</Alert>}
      {success && <Alert severity="success" className="mb-3">{success}</Alert>}

      <form onSubmit={createVisit} className="grid gap-4 max-w-2xl" data-allow-submit="true">
        <TextField
          select
          required
          fullWidth
          label="Encounter Type"
          name="visitType"
          value={form.visitType}
          onChange={handleChange}
          InputLabelProps={{ style: { color: 'inherit' } }}
          InputProps={{ style: { color: 'inherit' } }}
        >
          {VISIT_TYPES.map((item) => (
            <MenuItem key={item} value={item}>{item}</MenuItem>
          ))}
        </TextField>

        {maternalContextVisible && (
          <TextField
            select
            fullWidth
            label="Maternal Care Context"
            name="maternalContext"
            value={form.maternalContext}
            onChange={handleChange}
            helperText="Antenatal and postnatal service areas appear only for the relevant clinical context."
            InputLabelProps={{ style: { color: 'inherit' } }}
            InputProps={{ style: { color: 'inherit' } }}
          >
            {MATERNAL_CONTEXT_OPTIONS.map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          required
          fullWidth
          label="Service Area"
          name="visitCategory"
          value={category}
          onChange={handleChange}
          helperText="Filtered using age group, gender and maternal-care context."
          InputLabelProps={{ style: { color: 'inherit' } }}
          InputProps={{ style: { color: 'inherit' } }}
        >
          {visibleCategories.map((item) => (
            <MenuItem key={item} value={item}>{item}</MenuItem>
          ))}
        </TextField>

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
            Chief Complaints
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Add complaints one by one in clinical chronology. Each complaint has a Duration and Unit; use the arrows to reorder when needed.
          </Typography>

          {(chiefComplaints.length ? chiefComplaints : [emptyChiefComplaint(0)]).map((row, index) => (
            <Box
              key={`chief-complaint-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md: 'minmax(360px, 1fr) 120px 150px auto',
                },
                gap: 1,
                alignItems: 'center',
                mb: 1,
                width: '100%',
              }}
            >
              <Autocomplete
                freeSolo
                options={COMPLAINT_OPTIONS}
                value={row.complaint || ''}
                onChange={(_event, value) => {
                  const label = typeof value === 'string' ? value : String(value || '');
                  const def = findComplaintDefinition(label);
                  updateChiefComplaint(index, {
                    complaint: def?.label || label,
                    complaintCode: def?.code || '',
                  });
                }}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') updateChiefComplaint(index, { complaint: value, complaintCode: '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chief Complaint"
                    placeholder="Select or type complaint"
                    fullWidth
                  />
                )}
                sx={{ minWidth: 0 }}
              />

              <TextField
                type="number"
                fullWidth
                label="Duration"
                value={row.durationValue ?? ''}
                onChange={(event) => updateChiefComplaint(index, { durationValue: event.target.value })}
                inputProps={{ min: 1, step: 1 }}
              />

              <TextField
                select
                fullWidth
                label="Unit"
                value={row.durationUnit || 'Days'}
                onChange={(event) => updateChiefComplaint(index, { durationUnit: event.target.value })}
              >
                {DURATION_UNITS.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
              </TextField>

              <Box
                sx={{
                  display: 'flex',
                  gap: 0.25,
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  minWidth: { md: 116 },
                }}
              >
                <Tooltip title="Move up">
                  <span><IconButton size="small" onClick={() => moveChiefComplaint(index, -1)} disabled={index === 0}><ArrowUpward fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span><IconButton size="small" onClick={() => moveChiefComplaint(index, 1)} disabled={index >= chiefComplaints.length - 1}><ArrowDownward fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title="Remove complaint">
                  <span><IconButton size="small" onClick={() => removeChiefComplaint(index)} disabled={!chiefComplaints.length}><Delete fontSize="small" /></IconButton></span>
                </Tooltip>
              </Box>
            </Box>
          ))}

          <Button size="small" variant="outlined" startIcon={<Add />} onClick={addChiefComplaint}>
            Add complaint
          </Button>
        </Box>

        <Alert severity="info">
          <strong>Clinical pathway:</strong> {pathway || 'Will be derived from the service area and chief complaints'}
          {primaryPresentingProblem ? ` • Primary complaint: ${primaryPresentingProblem}` : ''}
          {derivedClinicalContext.activeClinicalModules?.length
            ? ` • Active modules: ${derivedClinicalContext.activeClinicalModules.join(', ')}`
            : ''}
        </Alert>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Other complaint / additional details"
          name="presentingComplaint"
          value={form.presentingComplaint}
          onChange={handleChange}
          placeholder="Free text for details not captured by the structured complaint rows"
          InputLabelProps={{ style: { color: 'inherit' } }}
          InputProps={{ style: { color: 'inherit' } }}
        />

        <TextField
          select
          required
          fullWidth
          label="Triage Priority"
          name="triagePriority"
          value={form.triagePriority}
          onChange={handleChange}
          InputLabelProps={{ style: { color: 'inherit' } }}
          InputProps={{ style: { color: 'inherit' } }}
        >
          {TRIAGE_PRIORITIES.map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>

        {redFlags.length > 0 && (
          <Alert
            severity={
              form.triagePriority === 'emergency'
                ? 'error'
                : form.triagePriority === 'priority'
                  ? 'warning'
                  : 'info'
            }
          >
            <strong>Immediate red-flag check:</strong>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              {redFlags.map((item) => <li key={item}>{item}</li>)}
            </Box>
            Clinical care and escalation must not be delayed to complete the routine visit form.
          </Alert>
        )}

        {['priority', 'emergency'].includes(form.triagePriority) && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            required={form.triagePriority === 'emergency'}
            label={
              form.triagePriority === 'emergency'
                ? 'Emergency Red Flag / Immediate Concern'
                : 'Priority Concern'
            }
            name="redFlagSummary"
            value={form.redFlagSummary}
            onChange={handleChange}
            placeholder="Record the finding, immediate action and person informed"
            InputLabelProps={{ style: { color: 'inherit' } }}
            InputProps={{ style: { color: 'inherit' } }}
          />
        )}

        <Box sx={{ mt: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            CMFM Care Route
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Every CMFM OPD citizen has already been registered in the State system and has an OPD slip.
            Record whether the citizen attended the CMFM teaching team after registration or was specifically
            referred / assigned by the State MO or CHC. Existing State identifiers remain linked in CMFM records.
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,minmax(0,1fr))', gap: 2 }}>
            <TextField
              select
              fullWidth
              label="CMFM Care Route"
              name="registrationSource"
              value={form.registrationSource || 'cmfm_direct'}
              onChange={handleChange}
              sx={{ gridColumn: 'span 12' }}
              InputLabelProps={{ style: { color: 'inherit' } }}
              InputProps={{ style: { color: 'inherit' } }}
            >
              {REGISTRATION_SOURCE_OPTIONS.map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>

            {isHmisLinked && (
              <>
                <TextField
                  fullWidth
                  label="External System"
                  name="externalSystem"
                  value={form.externalSystem || 'state_hmis'}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="HMIS Patient ID"
                  name="externalPatientId"
                  value={form.externalPatientId || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="HMIS Registration Number"
                  name="externalRegistrationNumber"
                  value={form.externalRegistrationNumber || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="HMIS Visit / Encounter ID"
                  name="externalVisitId"
                  value={form.externalVisitId || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="HMIS Token Number"
                  name="externalTokenNumber"
                  value={form.externalTokenNumber || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  type="date"
                  label="HMIS Registration Date"
                  name="externalRegistrationDate"
                  value={form.externalRegistrationDate || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ shrink: true, style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Reason for Referral / Assignment to CMFM"
                  name="referralReason"
                  value={form.referralReason || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: 'span 12' }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="Referred By — Name"
                  name="referredByName"
                  value={form.referredByName || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                <TextField
                  fullWidth
                  label="Referred By — Role"
                  name="referredByRole"
                  value={form.referredByRole || ''}
                  onChange={handleChange}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  InputLabelProps={{ style: { color: 'inherit' } }}
                  InputProps={{ style: { color: 'inherit' } }}
                />

                {!hmisReferencePresent && (
                  <Alert severity="warning" sx={{ gridColumn: 'span 12' }}>
                    Enter at least one HMIS patient ID, registration number, or token number.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Box>

        <TextField
          select
          required
          fullWidth
          label="Health Centre"
          name="centre"
          value={normalizeCentre(form.centre)}
          onChange={handleChange}
          InputLabelProps={{ style: { color: 'inherit' } }}
          InputProps={{ style: { color: 'inherit' } }}
        >
          {centreOptions.map((item) => (
            <MenuItem key={item} value={item}>{item}</MenuItem>
          ))}
        </TextField>

        <Alert severity="info">
          Newborn, young-infant, infant, under-five, school-age child, adolescent, adult and
          geriatric contexts are separated. The Visit now stores an immutable age snapshot,
          maternal context, triage information and any State HMIS linkage. Drafts remain
          citizen-scoped, while History and later encounter steps use this same Visit context.
        </Alert>

        <Box className="flex gap-2 mt-2">
          <Button type="submit" variant="contained" disabled={disabled}>
            {loading ? 'Starting…' : 'Start Visit'}
          </Button>

          <Button type="button" variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
}
